import {CodeViewError} from '@github-ui/code-view-shared/components/CodeViewError'
import {ActionProvider, type FilesPageAction} from '@github-ui/code-view-shared/contexts/ActionContext'
import {CopilotSettingsProvider} from '@github-ui/code-view-shared/contexts/CopilotSettingsContext'
import {EditPermissionsProvider} from '@github-ui/code-view-shared/contexts/EditPermissionsContext'
import {PathProvider} from '@github-ui/code-view-shared/contexts/PathContext'
import {RefInfoProvider} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import {useCanonicalObject} from '@github-ui/code-view-shared/hooks/use-canonical-object'
import type {DataRouterErrorPayload, DataRouterFilePagePayload} from '@github-ui/code-view-types'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import React, {useEffect} from 'react'

import {useUpdateRefInfoAndPath} from '../hooks/use-update-ref-info-and-path'
import styles from './CodeView.module.css'

export function CodeView({
  payload,
  action,
  children,
}: {
  payload: DataRouterFilePagePayload | DataRouterErrorPayload
  action: FilesPageAction
  children?: () => React.ReactNode
}) {
  const {path} = payload

  const refInfo = useCanonicalObject(payload.refInfo)

  useUpdateRefInfoAndPath(refInfo, path)

  useEffect(() => {
    let frameId: number | undefined
    // scroll to top of code nav on file change if not going to a specific line and already scrolled down
    if (!window.location.hash && window.scrollY > 0) {
      const codeViewHeader = document.querySelector('#StickyHeader') as HTMLElement
      if (codeViewHeader) {
        codeViewHeader.style.position = 'relative'
        codeViewHeader.scrollIntoView()
        frameId = requestAnimationFrame(() => {
          codeViewHeader.style.position = 'sticky'
        })
      }
    }
    return () => {
      if (frameId !== undefined) cancelAnimationFrame(frameId)
    }
  }, [payload.path])

  return (
    <PathProvider path={path}>
      <ActionProvider action={action}>
        <CopilotSettingsProvider
          copilotAccessAllowed={payload.copilotAccessAllowed ?? false}
          copilotSpacesEnabled={payload.copilotSpacesEnabled ?? false}
        >
          <RefInfoProvider refInfo={refInfo}>
            <EditPermissionsProvider
              canEdit={refInfo.canEdit}
              canEditOnDefaultBranch={refInfo.canEditOnDefaultBranch}
              fileExistsOnDefault={refInfo.fileExistsOnDefault}
            >
              {payload.error ? (
                <CodeViewError {...payload.error} />
              ) : (
                <ErrorBoundary boundaryName="code-view-top-level">
                  <div className={styles.contentWrapper}>{children?.()}</div>
                </ErrorBoundary>
              )}
            </EditPermissionsProvider>
          </RefInfoProvider>
        </CopilotSettingsProvider>
      </ActionProvider>
    </PathProvider>
  )
}
